import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users, BarChart3, Filter } from 'lucide-react';
import { fetchRfInboxRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import SkillGapTable from '../components/SkillGapTable';
import '../EvaluationDesign.css';

export default function RfGapPage() {
    const dispatch = useDispatch();
    const { inboxRf, loading } = useSelector(state => state.skillEvaluation || { inboxRf: [] });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL");

    useEffect(() => {
        dispatch(fetchRfInboxRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const globalList = useMemo(() => {
        return (inboxRf || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            poste: e.userEmployeeCode || "Collaborateur",
            color: '#204385'
        }));
    }, [inboxRf]);

    const filteredUsers = globalList.filter(u => {
        const uStatut = typeof u.statut === 'string' ? u.statut : u.statut?.name;
        const matchSearch = u.nom?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
        const matchStatus = filterStatus === "ALL" || uStatut === filterStatus;
        return matchSearch && matchCampagne && matchStatus;
    });

    const gapData = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];

        return selectedUser.niveaux.map(n => ({
            skill: n.skillNom,
            obtenu: n.scoreObtenu || n.scoreRf || n.scoreManager || n.scoreCollab || 0,
            requis: n.scoreRequis || 3
        }));
    }, [selectedUser]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Analyse Globale des Écarts (RH)</h1>
                    <p>Visualisez les écarts de compétences de l'ensemble des collaborateurs de l'entreprise.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="eval-filter-tabs">
                        <button className={`eval-tab-btn ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => setFilterStatus('ALL')}>Tous</button>
                        <button className={`eval-tab-btn ${filterStatus === 'VALIDEE' ? 'active' : ''}`} onClick={() => setFilterStatus('VALIDEE')}>Validés</button>
                    </div>
                </div>
            </div>

            <div className="eval-campaign-bar">
                <Filter size={16} className="text-slate-400" />
                <span className="eval-campaign-label">Campagne :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={(e) => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Toutes les campagnes --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input 
                                type="text" 
                                className="eval-input" 
                                placeholder="Rechercher un collaborateur..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="eval-list">
                        {loading && globalList.length === 0 ? (
                            <div className="eval-empty-small">Chargement...</div>
                        ) : filteredUsers.length > 0 ? filteredUsers.map(u => (
                            <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                <div className="eval-user-info">
                                    <div className="eval-user-name">{u.nom}</div>
                                    <div className="eval-user-sub">{u.poste} · {u.campagneNom}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="eval-empty-small">Aucun collaborateur trouvé</div>
                        )}
                    </div>
                </aside>

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-empty">
                            <Users size={64} className="eval-empty-icon" />
                            <h3>Vision Globale des Compétences</h3>
                            <p>Sélectionnez un collaborateur dans la liste pour analyser ses écarts par rapport aux requis du poste.</p>
                        </div>
                    ) : gapData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <BarChart3 size={18} /> Tableau des écarts : {selectedUser.nom}
                                </div>
                                <SkillGapTable data={gapData} />
                                <div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                                    Cette vue consolidée permet de piloter le plan de développement des compétences au niveau global.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation n'a été trouvée pour ce collaborateur dans les filtres sélectionnés.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
